import type { Address } from './models/ai-template-builder.model';

// Port of core's AnchorMatcher and AnchorBoundary: both sides must count occurrences alike, so change them together.

export interface TextRange {
  start: number;
  end: number;
}

/** `field` anchors may carry normalised fill-run tokens; `verbatim` anchors (clause options) never do. */
export type AnchorMode = 'field' | 'verbatim';

// The shortest run of each fill character that counts as a blank.
const RUN_MINIMUMS: ReadonlyMap<string, number> = new Map([
  ['_', 1],
  ['…', 1],
  ['.', 2],
  ...Array.from('!"#$%&\'()*+,-/:;<=>?@[\\]^`|~–—―·•', (character): [string, number] => [character, 3]),
]);

const WORD_CHARACTER = '[\\p{L}\\p{N}]';
const WORD_CHARACTER_PATTERN = new RegExp(`^${WORD_CHARACTER}$`, 'u');

export function findOccurrences(text: string, anchorText: string, mode: AnchorMode): TextRange[] {
  return occurrences(text, anchorText, mode, buildFillPattern);
}

export function resolveAddress(text: string, address: Address, mode: AnchorMode): TextRange | null {
  return findOccurrences(text, address.anchorText, mode)[address.occurrenceIndex] ?? null;
}

/** Same as `resolveAddress`, but builds the pattern of each anchor once for as long as the resolver lives. */
export function createAddressResolver(): typeof resolveAddress {
  const patterns = new Map<string, RegExp>();
  const fillPattern = (anchorText: string) => {
    const pattern = patterns.get(anchorText) ?? buildFillPattern(anchorText);

    patterns.set(anchorText, pattern);

    return pattern;
  };

  return (text, address, mode) =>
    occurrences(text, address.anchorText, mode, fillPattern)[address.occurrenceIndex] ?? null;
}

// matchAll scans a clone, so a reused global pattern keeps no state between texts.
function occurrences(
  text: string,
  anchorText: string,
  mode: AnchorMode,
  fillPattern: (anchorText: string) => RegExp,
): TextRange[] {
  if (anchorText === '') {
    return [];
  }

  if (mode === 'verbatim' || !containsFillToken(anchorText)) {
    return literalOffsets(text, anchorText).map((start) => ({ start, end: start + anchorText.length }));
  }

  return Array.from(text.matchAll(fillPattern(anchorText)))
    .filter((match) => boundaryHolds(text, match[0], match.index))
    .map((match) => ({ start: match.index, end: match.index + match[0].length }));
}

/** Redaction runs (`XX`, `XXXXX`) are never tokens: they always appear at their real length. */
export function containsFillToken(anchorText: string): boolean {
  const characters = Array.from(anchorText);

  return characters.some((_character, index) => tokenAt(characters, index) !== null);
}

function literalOffsets(text: string, anchorText: string): number[] {
  const offsets: number[] = [];
  let found = text.indexOf(anchorText);

  while (found !== -1) {
    if (boundaryHolds(text, anchorText, found)) {
      offsets.push(found);
    }

    found = text.indexOf(anchorText, found + 1);
  }

  return offsets;
}

function boundaryHolds(text: string, anchorText: string, offset: number): boolean {
  const characters = Array.from(anchorText);

  if (characters.length === 0) {
    return true;
  }

  return (
    !carriesOn(text, offset, characters, 'before') &&
    !carriesOn(text, offset + anchorText.length, characters.toReversed(), 'after')
  );
}

function carriesOn(text: string, position: number, fromEdge: string[], side: 'before' | 'after'): boolean {
  const [edge] = fromEdge;
  const minimum = RUN_MINIMUMS.get(edge);

  if (minimum !== undefined) {
    const needed = Math.max(1, minimum - leadingRun(fromEdge));
    const run = edge.repeat(needed);
    const start = side === 'before' ? position - run.length : position;

    return start >= 0 && text.slice(start, start + run.length) === run;
  }

  return isWordCharacter(edge) && isWordCharacter(characterBeside(text, position, side));
}

function characterBeside(text: string, position: number, side: 'before' | 'after'): string {
  if (side === 'after') {
    const codePoint = text.codePointAt(position);

    return codePoint === undefined ? '' : String.fromCodePoint(codePoint);
  }

  return Array.from(text.slice(Math.max(0, position - 2), position)).at(-1) ?? '';
}

function leadingRun(characters: string[]): number {
  const firstOther = characters.findIndex((character) => character !== characters[0]);

  return firstOther === -1 ? characters.length : firstOther;
}

function isWordCharacter(character: string): boolean {
  return WORD_CHARACTER_PATTERN.test(character);
}

function tokenAt(characters: string[], index: number): string | null {
  const character = characters[index];
  const length = tokenLength(character);
  const isToken = characters.slice(index, index + length).join('') === character.repeat(length);

  return isToken && RUN_MINIMUMS.has(character) ? character : null;
}

function tokenLength(fill: string): number {
  return fill === '…' ? 1 : 3;
}

function escapeRegExp(text: string): string {
  return text.replace(/[\^$\\.*+?()[\]{}|/]/g, '\\$&');
}

// JS has no possessive quantifiers: a lookahead capture plus its backreference cannot be backtracked into.
function createAtomic(): (pattern: string) => string {
  let groupCount = 0;

  return (pattern) => {
    groupCount += 1;

    return `(?=(?<atomic${groupCount}>${pattern}))\\k<atomic${groupCount}>`;
  };
}

function buildFillPattern(anchorText: string): RegExp {
  const atomic = createAtomic();
  const characters = Array.from(anchorText);
  let pattern = '';
  let index = 0;

  while (index < characters.length) {
    const fill = tokenAt(characters, index);

    if (fill === null) {
      pattern += escapeRegExp(characters[index]);
      index += 1;
      continue;
    }

    index += tokenLength(fill);

    // An anchor that copies more of the run than the token must not leave a literal tail behind.
    while (characters[index] === fill) {
      index += 1;
    }

    const copied = copiedRuns(characters.slice(index));

    pattern += blankPattern(fill, copied, atomic);
    index += Array.from(copied.join('')).length;
  }

  return new RegExp(pattern, 'gu');
}

function copiedRuns(characters: string[]): string[] {
  const absorbed = new RegExp(`^${adjacentRuns((pattern) => pattern)}`, 'u').exec(characters.join(''))?.[0] ?? '';

  return absorbed.match(/(.)\1*/gu) ?? [];
}

// One whole blank of `fill`. A single period is a full stop; a row of periods one or two spaces apart is one blank.
function blankPattern(fill: string, copied: string[], atomic: (pattern: string) => string): string {
  const escapedFill = escapeRegExp(fill);
  const blank = () =>
    runPattern(fill, atomic) +
    copied.map((run) => runPattern(Array.from(run)[0], atomic)).join('') +
    atomic(adjacentRuns(atomic));
  const alternatives = [
    `(?=${escapedFill.repeat(3)})${blank()}`,
    `(?<!${WORD_CHARACTER})${blank()}`,
    `${blank()}(?!${WORD_CHARACTER})`,
  ];

  if (fill === '.' && copied.length === 0) {
    alternatives.push(`(?<!\\S)\\.${atomic('(?:[\\u0020\\u00A0]{1,2}\\.(?!\\S))+')}`);
  }

  return `(?<!${escapedFill})${notInsideARun()}(?:${alternatives.join('|')})`;
}

function adjacentRuns(atomic: (pattern: string) => string): string {
  const runs = Array.from(RUN_MINIMUMS.keys(), (character) => runPattern(character, atomic));

  return `(?:${runs.join('|')})*`;
}

function runPattern(fill: string, atomic: (pattern: string) => string): string {
  return atomic(`${escapeRegExp(fill)}{${RUN_MINIMUMS.get(fill) ?? 1},}`);
}

function notInsideARun(): string {
  return Array.from(RUN_MINIMUMS, ([character, minimum]) => `(?<!${escapeRegExp(character).repeat(minimum)})`).join('');
}
